import { useState, useEffect, useMemo, useCallback } from 'react';
import { useApi } from '../../../containers/ApiContainer';
import { useActiveFBOs } from '../../../containers/ActiveFBOContainer';
import { InboxItem, GroupedInboxItems, InboxFilters } from '../types';

/**
 * Hook for fetching and managing operations inbox data
 * Fetches from operations_inbox_v view which combines quarantine + unassigned trips
 */
export function useOperationsInboxData(filters: InboxFilters) {
  const { postgrest } = useApi();
  const { activeFBO } = useActiveFBOs();
  const [items, setItems] = useState<InboxItem[]>([]);
  const [snoozedItems, setSnoozedItems] = useState<InboxItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Fetch inbox items
  const fetchItems = useCallback(async () => {
    if (!activeFBO?.id || !postgrest) return;

    try {
      setIsLoading(true);
      setError(null);

      // Fetch active items (not snoozed)
      const { data: activeData, error: activeError } = await postgrest
        .from('operations_inbox_v')
        .select('*')
        .eq('fbo_id', activeFBO.id)
        .order('item_type', { ascending: true })
        .order('days_until_arrival', { ascending: true, nullsFirst: true })
        .order('created_at', { ascending: false });

      if (activeError) throw new Error(activeError.message);

      setItems(activeData || []);

      // Fetch snoozed items separately (need to query with snoozed_until > now)
      if (filters.showSnoozed) {
        const { data: snoozedData, error: snoozedError } = await postgrest
          .from('inbox_item_state')
          .select(`
            id,
            fbo_id,
            pending_tenant_id,
            trip_id,
            snoozed_until,
            snoozed_by,
            snoozed_at,
            pending_tenant:flightbridge_pending_tenants!pending_tenant_id(
              id, tail_number, operator_name, aircraft_type_hint, source, created_at
            ),
            trip:trip!trip_id(
              id, arrival_date, departure_date, status,
              tenant:tenant_id(
                tail_number, owner_manager, aircraft_id,
                aircraft:aircraft_id(make, model, wingspan, length)
              )
            )
          `)
          .eq('fbo_id', activeFBO.id)
          .gt('snoozed_until', new Date().toISOString());

        if (!snoozedError && snoozedData) {
          // Transform snoozed items to InboxItem format
          const transformedSnoozed: InboxItem[] = snoozedData.map((item: any) => {
            if (item.pending_tenant) {
              return {
                item_type: 'quarantine' as const,
                item_id: item.pending_tenant.id,
                fbo_id: item.fbo_id,
                tail_number: item.pending_tenant.tail_number,
                operator_name: item.pending_tenant.operator_name,
                aircraft_type_hint: item.pending_tenant.aircraft_type_hint,
                item_source: item.pending_tenant.source,
                created_at: item.pending_tenant.created_at,
                trip_id: null,
                arrival_date: null,
                departure_date: null,
                trip_status: null,
                aircraft_id: null,
                aircraft_make: null,
                aircraft_model: null,
                wingspan: null,
                aircraft_length: null,
                hangar_id: null,
                ramp_id: null,
                location_name: null,
                location_type: 'unassigned',
                inbox_state_id: item.id,
                snoozed_until: item.snoozed_until,
                snoozed_by: item.snoozed_by,
                snoozed_at: item.snoozed_at,
                days_until_arrival: null,
              };
            } else if (item.trip) {
              const tenant = item.trip.tenant;
              const aircraft = tenant?.aircraft;
              return {
                item_type: 'unassigned_trip' as const,
                item_id: item.trip.id,
                fbo_id: item.fbo_id,
                tail_number: tenant?.tail_number || 'Unknown',
                operator_name: tenant?.owner_manager,
                aircraft_type_hint: aircraft ? `${aircraft.make} ${aircraft.model}` : null,
                item_source: 'manual',
                created_at: item.trip.created_at,
                trip_id: item.trip.id,
                arrival_date: item.trip.arrival_date,
                departure_date: item.trip.departure_date,
                trip_status: item.trip.status,
                aircraft_id: tenant?.aircraft_id,
                aircraft_make: aircraft?.make,
                aircraft_model: aircraft?.model,
                wingspan: aircraft?.wingspan,
                aircraft_length: aircraft?.length,
                hangar_id: null,
                ramp_id: null,
                location_name: null,
                location_type: 'unassigned',
                inbox_state_id: item.id,
                snoozed_until: item.snoozed_until,
                snoozed_by: item.snoozed_by,
                snoozed_at: item.snoozed_at,
                days_until_arrival: null,
              };
            }
            return null;
          }).filter(Boolean) as InboxItem[];

          setSnoozedItems(transformedSnoozed);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch inbox'));
    } finally {
      setIsLoading(false);
    }
  }, [activeFBO?.id, postgrest, filters.showSnoozed]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Filter items by search query
  const filteredItems = useMemo(() => {
    if (!filters.search) return items;

    const query = filters.search.toLowerCase();
    return items.filter(item =>
      item.tail_number?.toLowerCase().includes(query) ||
      item.operator_name?.toLowerCase().includes(query) ||
      item.aircraft_type_hint?.toLowerCase().includes(query) ||
      item.aircraft_make?.toLowerCase().includes(query) ||
      item.aircraft_model?.toLowerCase().includes(query)
    );
  }, [items, filters.search]);

  // Group items by action type
  const groups: GroupedInboxItems = useMemo(() => {
    const unknownAircraft: InboxItem[] = [];
    const needsRouting: InboxItem[] = [];

    filteredItems.forEach(item => {
      if (item.item_type === 'quarantine') {
        unknownAircraft.push(item);
      } else {
        needsRouting.push(item);
      }
    });

    return {
      unknownAircraft,
      needsRouting,
      snoozed: snoozedItems,
    };
  }, [filteredItems, snoozedItems]);

  // Calculate counts
  const totalCount = items.length;
  const actionableCount = groups.unknownAircraft.length + groups.needsRouting.length;

  return {
    items,
    setItems,
    groups,
    totalCount,
    actionableCount,
    isLoading,
    error,
    refetch: fetchItems,
  };
}
